import React from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from 'primereact/button'
import { Checkbox } from 'primereact/checkbox'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Dropdown } from 'primereact/dropdown'
import { InputNumber } from 'primereact/inputnumber'
import { InputText } from 'primereact/inputtext'

import { calculateBreakpointWidth } from '../../../../../../../utils/helper/calculateBreakpointWidth'
import { ConfigurableComponent } from '../../../../../../../utils/wrapper'
import { useConfigContext } from '../../../../../../ui-config'
import { useDeliveryForecastDespatchAdviceItemPackagingItemById } from '../../../../../data/hooks'
import { BatchesTable } from './BatchesTable'
import { PackagingMaterialTable } from './PackagingMaterialTable'

type Props = {
  despatchAdviceId: string
  itemId: string
  packagingId: string
  id: string
  isVisible: boolean
  onHide: () => void
}

export const Info: React.FC<Props> = ({
  despatchAdviceId,
  itemId,
  packagingId,
  id,
  isVisible,
  onHide,
}) => {
  const { t, i18n } = useTranslation()
  const { language } = i18n
  const { uiConfig: config } = useConfigContext()

  const { data, isLoading, error } =
    useDeliveryForecastDespatchAdviceItemPackagingItemById(
      despatchAdviceId,
      itemId,
      packagingId,
      id,
    )

  // ************************************************************
  // components
  // ************************************************************
  // Label
  const labelLabel = <label htmlFor="label" className="text-sm"></label>

  const labelComponent = (
    <Dropdown
      id="label"
      className="w-full"
      value={data?.Label}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const labelWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={labelComponent}
      label={labelLabel}
      isLoading={isLoading}
    />
  )

  // HuNumber
  const huNumberLabel = <label className="text-sm" htmlFor="huNumber"></label>

  const huNumberComponent = (
    <InputText
      id="huNumber"
      value={data?.HuNumber}
      className="w-full"
      disabled
    />
  )

  const huNumberWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={huNumberComponent}
      label={huNumberLabel}
      isLoading={isLoading}
    />
  )

  // IsOuterPackaging
  const isOuterPackagingLabel = (
    <label htmlFor="isOuterPackaging" className="text-sm"></label>
  )

  const isOuterPackagingComponent = (
    <div
      className="flex align-items-center"
      id="isOuterPackaging"
      data-checkbox
    >
      <Checkbox
        id="isOuterPackaging"
        inputId="isOuterPackaging"
        name="isOuterPackaging"
        value="yes"
        checked={!!data?.IsOuterPackaging}
        disabled
      />
      <label htmlFor="isOuterPackaging" className="ml-2">
        {t(
          'masterDataManagement.packagingData.table.edit.dialog.form.isStackable.checkbox.label',
        )}
      </label>
    </div>
  )

  const isOuterPackagingWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={isOuterPackagingComponent}
      label={isOuterPackagingLabel}
      isLoading={isLoading}
    />
  )

  // TODO: Check if this should be visible
  // // SelectMaster
  // const selectMasterLabel = (
  //   <label htmlFor="selectMaster" className="text-sm"></label>
  // )

  // const valueTemplate = (option: Master, props: DropdownProps) => {
  //   if (option) {
  //     return itemTemplate(option)
  //   }

  //   return <>{props.placeholder}</>
  // }

  // const itemTemplate = (option: Master) => {
  //   return <>{option.name}</>
  // }

  // const selectMasterComponent = data?.PackagingType === "Single"  ? (
  //   <Dropdown
  //     id="selectMaster"
  //     className="w-full"
  //     value={data?.Label === "Single" && data.}
  //     valueTemplate={valueTemplate}
  //     itemTemplate={itemTemplate}
  //     options={masters}
  //     optionLabel="code"
  //     optionValue="code"
  //     disabled
  //     showClear
  //   />
  // ): (<></>)

  // const selectMasterWrapper = (
  //   <ConfigurableComponent
  //     config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
  //     component={selectMasterComponent}
  //     label={selectMasterLabel}
  //     isLoading={isLoading}
  //   />
  // )

  // Quantity
  const quantityLabel = <label htmlFor="quantity" className="text-sm"></label>

  const quantityComponent =
    data?.PackagingType === 'Single' ? (
      <InputNumber
        id="quantity"
        value={data.Quantity?.Value}
        className="w-full"
        showButtons
        locale={language}
        min={1}
        disabled
      />
    ) : (
      <></>
    )

  const quantityWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={quantityComponent}
      label={quantityLabel}
      isLoading={isLoading}
    />
  )

  // QuantityUnit
  const quantityUnitLabel = (
    <label htmlFor="quantityUnit" className="text-sm"></label>
  )

  const quantityUnitComponent =
    data?.PackagingType === 'Single' ? (
      <Dropdown
        id="quantityUnit"
        className="w-full"
        value={data?.Quantity?.Unit}
        optionLabel="code"
        optionValue="code"
        disabled
      />
    ) : (
      <></>
    )

  const quantityUnitWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={quantityUnitComponent}
      label={quantityUnitLabel}
      isLoading={isLoading}
    />
  )

  // Weight
  const weightLabel = <label htmlFor="weight" className="text-sm"></label>

  const weightComponent = (
    <InputNumber
      id="weight"
      value={data?.TareWeight?.Value}
      className="w-full"
      showButtons
      locale={language}
      min={0}
      minFractionDigits={3}
      disabled
    />
  )

  const weightWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={weightComponent}
      label={weightLabel}
      isLoading={isLoading}
    />
  )

  // WeightUnit
  const weightUnitLabel = (
    <label htmlFor="weightUnit" className="text-sm"></label>
  )

  const weightUnitComponent = (
    <Dropdown
      id="weightUnit"
      className="w-full"
      value={data?.TareWeight?.Unit}
      optionLabel="code"
      optionValue="code"
      disabled
    />
  )

  const weightUnitWrapper = (
    <ConfigurableComponent
      config={config.deliveryForecastDespatchAdvice.infoPackagingItem}
      component={weightUnitComponent}
      label={weightUnitLabel}
      isLoading={isLoading}
    />
  )

  // ************************************************************
  // dialog components
  // ************************************************************
  // header
  const header = (
    <div className="flex align-content-center flex-wrap">
      <span className="mr-3">
        {t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.info.dialog.title',
          { position: data?.Position },
        )}
      </span>
    </div>
  )

  // footer
  const footer = (
    <div className="pt-3">
      <Button
        label={t(
          'deliveryForecastDespatchAdvice.shippingDetails.packagingTable.info.dialog.close.label',
        )}
        onClick={onHide}
        className="w-full md:w-auto"
        icon={'pi pi-check'}
        type="submit"
      />
    </div>
  )

  const isMaster = data?.PackagingType === 'Master'
  const isSingle = data?.PackagingType === 'Single'
  const isHelper = data?.PackagingType === 'Helper'
  const hasBatches = data?.Batches && data?.Batches.length > 0

  return (
    <Dialog
      header={header}
      visible={isVisible}
      style={{ width: '75rem' }}
      breakpoints={{ [calculateBreakpointWidth(75)]: '90vw' }}
      footer={footer}
      onHide={onHide}
      blockScroll
    >
      <div className="formgrid grid">
        {/* labelWrapper */}
        <div className="col-12 md:col-6 lg:col-4 xl:col-3">{labelWrapper}</div>

        {/* huNumberWrapper */}
        {(isMaster || isSingle) && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {huNumberWrapper}
          </div>
        )}

        {/* isOuterPackagingWrapper */}
        {(isMaster || isSingle) && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {isOuterPackagingWrapper}
          </div>
        )}

        {/* selectMasterWrapper */}
        {/* {(isSingle || isHelper) && (
            <div className="col-12 md:col-6 lg:col-4 xl:col-3">
              {selectMasterWrapper}
            </div>
          )} */}

        {/* quantityWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {quantityWrapper}
          </div>
        )}

        {/* quantityUnitWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {quantityUnitWrapper}
          </div>
        )}

        {/* weightWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {weightWrapper}
          </div>
        )}

        {/* weightUnitWrapper */}
        {isSingle && (
          <div className="col-12 md:col-6 lg:col-4 xl:col-3">
            {weightUnitWrapper}
          </div>
        )}
      </div>

      {isSingle && hasBatches && (
        <>
          <Divider className="my-5" />

          <BatchesTable
            batches={data.Batches}
            config={
              config.deliveryForecastDespatchAdvice.infoPackagingItem.batchTable
            }
          />
        </>
      )}

      <Divider className="my-5" />

      <PackagingMaterialTable
        config={
          config.deliveryForecastDespatchAdvice.infoPackagingItem.packagingTable
        }
        packagingData={data?.PackagingData}
        isLoading={isLoading}
      />
    </Dialog>
  )
}
